/* Copyright (c) 2023-2026, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { merge, Observable, of, ReplaySubject, Subscription, timer } from 'rxjs';
import { distinctUntilChanged, map, mapTo, take } from 'rxjs/operators';
import { Commands } from 'bridge-commands/commands';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { PlatformDetectorService } from 'crimson/lib/platform-detector/platform-detector.service';
import {
    Plugins,
    ModulesActiveNotificationReturn,
    PluginItem,
    State1,
    PluginLoadInfo,
    PluginLoadStatus
} from './interfaces/plugins-notification.interfaces';

@Injectable({
    providedIn: 'root'
})
export class PluginsNotificationService {
    private readonly logger: Logger;
    private readonly defaultPluginTimeout = 30000;
    private mandatoryPluginsLoaded: ReplaySubject<PluginItem>;
    private optionalPluginLoaded: ReplaySubject<PluginItem>;
    private timerSub: Subscription;
    private modulesActivationNotificationSub: Subscription;
    private pluginsActivationMap = new Map<string, ReplaySubject<boolean>>();
    private bridgeCommandCefExecuteCommand: Function;
    private hasPluginActivationNotificationTimedOut = new ReplaySubject<boolean>(1);
    private pluginLoadStartTime: number;
    private pluginsLoadInfoMap = new Map<string, PluginLoadInfo>();
    private pluginsLoadInfoMapSub$ = new ReplaySubject<Map<string, PluginLoadInfo>>(1);
    private mandatoryPluginNames = [];
    private optionalPluginNames = [];

    constructor(
        loggingService: LoggingService,
        private plugins: Plugins
    ) {
        this.logger = loggingService.getLogger('PluginsNotificationService');
        this.mandatoryPluginsLoaded = new ReplaySubject<PluginItem>(1);
        this.optionalPluginLoaded = new ReplaySubject<PluginItem>(1);
        this.mandatoryPluginNames = plugins.mandatoryModuleNames.map(value => value.toLowerCase()) ?? [];
        this.optionalPluginNames = plugins.optionalModulesNames.map(value => value.toLowerCase()) ?? [];
        this.logger.info(
            `Mandatory plugins list ${this.mandatoryPluginNames} \n optional plugins list ${this.optionalPluginNames}`
        );
        this.getAllPluginNames().forEach(pluginName => {
            this.pluginsLoadInfoMap.set(pluginName, {
                loadStatus: PluginLoadStatus.UNKNOWN,
                loadDurationMs: -1
            });
        });
    }

    isMandatoryPlugin(pluginName: string) {
        const plugin = pluginName.toLowerCase();
        return !!this.mandatoryPluginNames?.find(name => name === plugin);
    }

    isOptionalPlugin(pluginName: string) {
        const plugin = pluginName.toLowerCase();
        return !!this.optionalPluginNames?.find(name => name === plugin);
    }

    getCurrentPluginsLoadInfo(): Map<string, PluginLoadInfo> {
        return this.pluginsLoadInfoMap;
    }

    getAllPluginNames(): string[] {
        return [...this.mandatoryPluginNames, ...this.optionalPluginNames];
    }

    /**
     * @returns observable for the load status of all plugins
     * this emits only when the plugin load status has been resolved for all plugins
     */
    getAllPluginLoadInfo$(): Observable<Map<string, PluginLoadInfo>> {
        return this.pluginsLoadInfoMapSub$.asObservable();
    }

    /**
     * @param pluginKey
     * @returns observable for the load status of a plugin
     * this emits only when the plugin load status has been resolved
     */
    getPluginLoadInfoForPlugin$(pluginKey: string): Observable<PluginLoadInfo> {
        return this.isPluginLoaded$(pluginKey).pipe(
            map(_ => this.pluginsLoadInfoMap.get(pluginKey)),
            distinctUntilChanged((prev, curr) => {
                return prev?.loadStatus === curr?.loadStatus;
            })
        );
    }

    private handleModuleActivationNotification(modulesActiveNotificationReturn: ModulesActiveNotificationReturn) {
        let loadedMandatoryPlugins = new Set<string>();
        let loadedOptionalPlugins = new Set<string>();
        let failedOptionalPlugins = new Set<string>();

        for (const moduleElement of modulesActiveNotificationReturn.modules) {
            const pluginName = moduleElement.module;
            const isMandatory = this.isMandatoryPlugin(pluginName);
            const isOptional = this.isOptionalPlugin(pluginName);
            const isLoaded = moduleElement.state === State1.active;
            const pluginKey = pluginName.toLowerCase();

            if (!this.pluginsActivationMap.has(pluginKey)) {
                this.pluginsActivationMap.set(pluginKey, new ReplaySubject<boolean>(1));
            }

            const pluginInfo = this.pluginsLoadInfoMap.get(pluginKey);
            // Some plugins might be not loaded and get loaded later, we update if plugins get loaded later
            // If plugins fails later, need to update the status to failure
            if (
                pluginInfo?.loadStatus === PluginLoadStatus.UNKNOWN ||
                (pluginInfo?.loadStatus === PluginLoadStatus.SUCCESS && !isLoaded)
            ) {
                this.pluginsLoadInfoMap.set(pluginKey, {
                    ...pluginInfo,
                    loadStatus: isLoaded ? PluginLoadStatus.SUCCESS : PluginLoadStatus.FAILURE,
                    loadDurationMs: Date.now() - this.pluginLoadStartTime
                });
            }

            this.pluginsActivationMap.get(pluginKey).next(isLoaded);

            if (isMandatory) {
                if (isLoaded) {
                    loadedMandatoryPlugins.add(pluginName);
                } else {
                    this.mandatoryPluginsLoaded.next({ loaded: false, name: pluginName });
                    if (this.timerSub) {
                        this.timerSub.unsubscribe();
                    }
                }
            } else if (isOptional) {
                if (isLoaded) {
                    loadedOptionalPlugins.add(pluginName);
                } else {
                    failedOptionalPlugins.add(pluginName);
                }
            }
        }

        if (loadedMandatoryPlugins.size === this.plugins.mandatoryModuleNames.length) {
            // All mandatory plugins loaded
            this.mandatoryPluginsLoaded.next({ loaded: true });
        } else {
            // If mandatory plugins has failed optional plugins status should not be sent
            return;
        }

        const totalOptionalPluginEvaluated = loadedOptionalPlugins.size + failedOptionalPlugins.size;
        if (loadedOptionalPlugins.size === this.plugins.optionalModulesNames.length) {
            // All optionals plugin loaded
            this.optionalPluginLoaded.next({ loaded: true });
        } else if (failedOptionalPlugins.size > 0) {
            // All optional plugins evaluated and one or more failed
            if (failedOptionalPlugins.size === 1) {
                // Only one optional plugin failed
                const [failedPluginName] = failedOptionalPlugins;
                this.optionalPluginLoaded.next({ loaded: false, name: failedPluginName });
            } else {
                // Multiple optional plugin failed
                this.optionalPluginLoaded.next({ loaded: false });
            }
        }

        if (
            totalOptionalPluginEvaluated + loadedMandatoryPlugins.size ===
            this.plugins.mandatoryModuleNames.length + this.plugins.optionalModulesNames.length
        ) {
            // All plugins have been evaluated
            this.pluginsLoadInfoMapSub$.next(this.pluginsLoadInfoMap);
            if (this.timerSub) {
                this.timerSub.unsubscribe();
            }
        }
    }

    // Notify whether all mandatory plugins are loaded or either one has failed
    get mandatoryPluginsLoaded$(): Observable<PluginItem> {
        return this.mandatoryPluginsLoaded.asObservable().pipe(
            distinctUntilChanged((prev, curr) => {
                return prev.loaded === curr.loaded && prev.name === curr.name;
            })
        );
    }

    // Notify whether all mandatory plugins are loaded or either one or many has failed
    get optionalPluginsLoaded$(): Observable<PluginItem> {
        return this.optionalPluginLoaded.asObservable().pipe(
            distinctUntilChanged((prev, curr) => {
                return prev.loaded === curr.loaded && prev.name === curr.name;
            })
        );
    }

    private registerForModulesActiveNotification() {
        this.modulesActivationNotificationSub = this.bridgeCommandCefExecuteCommand(
            Commands.Common.Applicationcontrol.ModulesActiveNotification
        ).subscribe(
            (response: ModulesActiveNotificationReturn) => {
                this.logger.info('ModuleActiveNotification state ', response);
                if (response.modules.length > 0) {
                    this.handleModuleActivationNotification(response);
                    this.logger.info('plugins loaded', response.modules);
                }
            },
            error => {
                this.logger.error('MandatoryPluginsHandlerService failed with error: ', error);
                this.mandatoryPluginsLoaded.next({ loaded: false, name: '' });
                this.modulesActivationNotificationSub.unsubscribe();
            }
        );
    }

    /**
     * @param pluginName
     * @returns boolean observable for the state of plugin load for CEF
     * platform, for other platform it returns true
     */
    isPluginLoaded$(pluginName: string): Observable<boolean> {
        /* Platform-check-disable-next-line */
        if (!PlatformDetectorService.isCefPlatform()) {
            return of(true);
        }

        const plugin = pluginName.toLocaleLowerCase();
        if (!this.pluginsActivationMap.has(plugin)) {
            this.pluginsActivationMap.set(plugin, new ReplaySubject<boolean>(1));
        }

        if (this.isMandatoryPlugin(plugin)) {
            // If mandatory plugin check for the timeout, if already timed out send as plugin not active
            return merge(
                this.pluginsActivationMap.get(plugin).asObservable(),
                this.hasPluginActivationNotificationTimedOut
                    .asObservable()
                    .pipe(map(_ => this.pluginsLoadInfoMap.get(plugin)?.loadStatus === PluginLoadStatus.SUCCESS))
            ).pipe(distinctUntilChanged());
        }
        return this.pluginsActivationMap.get(plugin).asObservable().pipe(distinctUntilChanged());
    }

    init(executeCommandFunc: Function): void {
        this.logger.info('Initializing plugins notification service');
        this.bridgeCommandCefExecuteCommand = executeCommandFunc;
        this.pluginLoadStartTime = Date.now();
        this.registerForModulesActiveNotification();

        if (this.plugins.pluginTimeout) {
            this.triggerLoadingIndicator(true);
        }
    }

    private triggerLoadingIndicator(on: boolean): void {
        if (on) {
            if (this.timerSub) {
                this.timerSub.unsubscribe();
            }
            this.timerSub = timer(this.plugins.pluginTimeout ?? this.defaultPluginTimeout).subscribe(() => {
                let hasMandatoryPluginTimedOut = false;
                this.pluginsLoadInfoMap.forEach((info, pluginKey) => {
                    if (info.loadStatus === PluginLoadStatus.UNKNOWN) {
                        if (this.isMandatoryPlugin(pluginKey)) {
                            hasMandatoryPluginTimedOut = true;
                        }
                        info.loadStatus = PluginLoadStatus.TIMEDOUT;
                        info.loadDurationMs = -1;
                        if (!this.pluginsActivationMap.has(pluginKey)) {
                            this.pluginsActivationMap.set(pluginKey, new ReplaySubject<boolean>(1));
                        }
                        this.pluginsActivationMap.get(pluginKey).next(false);
                    }
                });
                // Few plugins found not loaded, raise plugin failure event
                // Timeout load error is only raised for mandatory plugins
                // as optional plugins are not critical for the application to run
                if (hasMandatoryPluginTimedOut) {
                    this.mandatoryPluginsLoaded.next({ loaded: false });
                }
                this.hasPluginActivationNotificationTimedOut.next(true);
                this.pluginsLoadInfoMapSub$.next(this.pluginsLoadInfoMap);
                this.modulesActivationNotificationSub.unsubscribe();
                this.timerSub.unsubscribe();
                this.timerSub = null;
            });
        } else {
            if (this.timerSub) {
                this.timerSub.unsubscribe();
            }
        }
    }
}
